import { LabelWithCircleContainer } from "common/components/ConfigurableCharts/PieChart/PieChart.elements"
import { ConfigurableChartAndLegend } from "common/components/ConfigurableLineChart"
import { DataLoader } from "common/components/DataLoader/DataLoader"
import { convertToZonedTimestamp } from "common/helpers/dateAndTimeHelpersV2"
import { useFleetRouter } from "common/pages/fleetV2/hooks"
import { useMachineInfo } from "common/pages/fleetV2/providers"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
 
type IntensifierLineChartWidgetProps = {
  int?: string
  data?: Record<string, unknown>[]
  startTime?: string
  endTime?: string
  colors?: Record<string, string>
  valueKeys?: string[]
  isLoading?: boolean
  error?: unknown
  isFetching?: boolean
  mapKeys?: Record<string, string>
  summary?: Record<string, unknown>
  summation?: boolean
  axis_formatter?: string
  popupMode?: boolean
  onBrushRangeChange?: (range: { startTime: number; endTime: number }) => void
}

type BrushScale = {
  invert: (value: number) => string | number | Date
}

type TickFormatterArgs = { label?: string }
type LegendFormatterArgs = {
  value?: unknown
  data?: { high?: number; labelKey?: string; [key: string]: unknown }
  label?: unknown
}
type TooltipFormatterArgs = {
  arr?: Record<string, unknown>[]
  group?: unknown
  innerHTML?: unknown
  value?: unknown
  color?: unknown
  title?: string
}

export const getMetricValue = (
  row: Record<string, unknown>,
  intensifierKey: string,
  metricKey = 'stroke_rate',
  aggregateKey = 'avg'
): number | undefined => {
  const nested = row?.[`${intensifierKey}_${metricKey}`] as Record<string, unknown> | undefined
  const nestedValue = nested?.[aggregateKey]

  if (typeof nestedValue === 'number' && Number.isFinite(nestedValue)) return nestedValue

  // Fallback for old payloads during rollout.
  const legacy = row?.[`${intensifierKey}_stroke_rate_per_minute`]
  const parsedLegacy = Number(legacy)
  return Number.isFinite(parsedLegacy) ? parsedLegacy : undefined
}


export const IntensifierLineChartWidget = (props: IntensifierLineChartWidgetProps = {}): JSX.Element => {

  const { timeZone} = useMachineInfo() as { timeZone: string }
  const [{ urlVars }, go] = useFleetRouter()

  const { t } = useTranslation(['mh']);

  const {
    int,
    data = [],
    startTime,
    endTime,
    colors = {},
    valueKeys,
    isLoading = false,
    error,
    isFetching = false,
    mapKeys = {},
    summary = {},
    summation = false,
    axis_formatter = 'date',
    popupMode = false,
    onBrushRangeChange
  } = props

  const rows = Array.isArray(data) ? data : []

  const inferredValueKeys = Array.from(
    new Set(
      rows.flatMap((row) =>
        Object.keys(row || {}).flatMap((key) => {
          const nestedMetricMatch = key.match(/^(int\d+)_stroke_rate$/)
          if (nestedMetricMatch?.[1]) return [nestedMetricMatch[1]]

          const legacyMetricMatch = key.match(/^(int\d+)_stroke_rate_per_minute$/)
          if (legacyMetricMatch?.[1]) return [legacyMetricMatch[1]]

          return []
        })
      )
    )
  )

  const valueKeysToUse = (Array.isArray(valueKeys) && valueKeys.length > 0
    ? valueKeys
    : summation && inferredValueKeys.length > 0
    ? inferredValueKeys
    : int
    ? [String(int)]
    : []) as string[]

  const computedChartData = useMemo(() => {
    return rows
      .map((row) => {
        const timestamp = String(row?.start_datetime_utc || row?.group || row?.timestamp || '')
        if (!timestamp) return undefined

        let total = 0
        let hasAnyValue = false

        const values = valueKeysToUse.reduce((acc: Record<string, unknown>, key) => {
          const isDisabled = Boolean(row?.[`${key}_disabled`])
          const metricValue = getMetricValue(row, key)

          if (isDisabled || metricValue === undefined) {
            acc[key] = undefined
            return acc
          }

          hasAnyValue = true
          total += metricValue
          acc[key] = metricValue
          return acc
        }, {})

        return {
          ...values,
          timestamp,
          total,
          hasAnyValue
        }
      })
      .filter(Boolean) as Record<string, unknown>[]
  }, [rows, valueKeysToUse])

  const isApiInProgress = Boolean(isLoading || isFetching)
  const chartData = computedChartData

  const hasData = chartData.length > 0 && chartData.some((item) => {
    if (summation) return Number.isFinite(Number(item?.total))

    if (!valueKeysToUse.length) return false
    return valueKeysToUse.some((key) => Number.isFinite(Number(item?.[key])))
  })

  const shouldShowBlockingLoader = isApiInProgress && !hasData

  if (!int && !valueKeys && !summation) return <></>

  const valueKey = valueKeys ? undefined : String(int)
  const singleSeriesColor = int ? colors[String(int)] : undefined

  const handleBrush = ([start, end]: number[], scaleX: BrushScale) => {
    const lStart = scaleX.invert(start)
    const rEnd = scaleX.invert(end)
    const startTime = new Date(lStart).getTime()
    const endTime = new Date(rEnd).getTime()

    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) return

    if (popupMode) {
      onBrushRangeChange?.({ startTime, endTime })
      return
    }

    const diffDays =
      ((endTime - startTime) / (1000 * 60 * 60 * 24)) * 0.9

    go('updateVars', {
      range: `${startTime},${endTime}`,
      aggregation_type: diffDays < 1 ? 'None' : undefined
    })
  }
 
  const chart_settings = {
    data: chartData,
    isLoading: Boolean(isLoading || isFetching),
    hasError: Boolean(error),
    isFetching,
    className: 'intensifier-widget-chart',
    chartType: 'lines',
    scaleType: 'time',
    timestampKey: 'timestamp',
    startTime,
    endTime,
    height: `250px`,   
    valueKey: summation ? 'total' : valueKey,
    valueKeys: summation ? undefined : valueKeys,
    color: valueKeys ? undefined : singleSeriesColor || 'blue',
    nice: false,
    handleBrush: !urlVars
      ? handleBrush
      : urlVars?.chart_type === 'point-to-point'
      ? undefined
      : handleBrush,
    format: {
      bottomTicks: ({ label }: TickFormatterArgs) => {
        if (!label) return <></>

        const [day, date, time] = convertToZonedTimestamp(label, timeZone, 'fullDay')?.split(', ') || []
        const [d, m] = date?.split(' ') || []

        if (axis_formatter === 'date') return <>{d}<br />{m}</>
        if (axis_formatter === 'day') return <>{day}<br />{date}</>

        return <>{time}<br />{date}</>
      },
      legendItems: (x: LegendFormatterArgs) => {
        const {value, data, label} = x || {}

        if(label && summation) return <>{t("Total stroke counts per minute")}</>
        if (label && !summation) return <>{label} {t('Stroke rate per minute')}</>

        if(value){
          const avg = Math.round(data?.high || 0).toFixed()
          const labelKey = String(data?.labelKey || '')
          const metricKey = String(data?.[labelKey] || '')
          const summaryMetric = summary?.[`${metricKey}_stroke_rate`]
          const summaryMax = Number((summaryMetric as Record<string, unknown> | undefined)?.max)

          if (Number.isFinite(summaryMax)) return `${Math.round(summaryMax)} Max SPM`
          return `${avg} Max SPM`
        }
      },
      tooltips: (x: TooltipFormatterArgs) => {
        const { arr, group, innerHTML, value, color, title } = x || {}
        const row = (arr?.[0] || {}) as Record<string, unknown>
        const total = Number(row?.total) || 0

        if (group || value || color) return <></>
        
        if (title) return convertToZonedTimestamp(title, timeZone, 'full')
         if (innerHTML)
          return (
            <>
              {summation ? (
                <div className="legend-label">
                  <LabelWithCircleContainer color={'blue'}>
                    <div className="legend-label">Total SPM</div>
                    <div className="sub-text value">: {Number(total).toFixed(1) || 0}</div>
                  </LabelWithCircleContainer>
                </div>
              ) : (
                <>
                  {valueKeysToUse.map((key) => {
                    const valueForKey = row?.[key]
                    const num = key.replace('int', '')
                    const label = mapKeys[key] || `Intensifier ${num}`
                    return (
                      <div>
                        <LabelWithCircleContainer key={key} color={colors[key]}>
                          <div className="legend-label">{label} </div>
                          <div className="sub-text value">{Number(valueForKey).toFixed(1) || 0}</div>
                        </LabelWithCircleContainer>
                      </div>
                    );
                  })}
                </>
              )}
            </>
          );
      }
    },
    colors,
    axisLabels:{
      left: t('Stroke Rate Per Minute'),
    },
    
    mapKeys
   
  } 

  const chart = (
    <DataLoader
      {...{
        isLoading: shouldShowBlockingLoader,
        hasError: Boolean(error),
        hasMessage: !isApiInProgress && !error && !hasData ? t('No data available') : undefined
      }}
    >
      {hasData && <ConfigurableChartAndLegend {...chart_settings} />}
    </DataLoader>
  )

  if (!popupMode) return chart

  return (
    <div className="intensifier-chart-slot">
      {chart}
      {hasData && isApiInProgress && (
        <div className="intensifier-chart-refresh-loader" data-testid="refresh-loader">
          <DataLoader isLoading />
        </div>
      )}
    </div>
  )
}